<template>
  <Teleport to="body">
    <Transition
      name="fade"
      appear>
      <div
        v-if="showPopUp"
        class="intro-popup-backdrop"
        aria-hidden="true"></div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition
      name="fade"
      appear>
      <div
        v-if="showPopUp"
        class="intro-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="intro-popup-title"
        @click.self="closePopUp">
        <div
          data-lenis-prevent
          class="intro-popup-content">
          <button
            ref="closeButton"
            class="intro-popup-close"
            type="button"
            aria-label="Close popup"
            @click="closePopUp">
            <span></span>
            <span></span>
          </button>

          <div class="intro-popup-scroll-body">
            <div class="intro-popup-copy">
              <h2
                id="intro-popup-title"
                v-html="popupOptions?.title"></h2>

              <img
                v-if="popupOptions?.image?.url"
                class="intro-popup-image"
                :src="popupOptions.image.url"
                :alt="popupOptions.image.alt || ''" />

              <div
                v-if="popupOptions?.buttons"
                class="intro-popup-action"
                @click="closePopUp">
                <a
                  v-for="(button, index) in popupOptions.buttons"
                  :key="`popup-button-${index}`"
                  class="intro-popup-button"
                  :href="button.button.url"
                  :target="button.button.target || '_self'"
                  :rel="
                    button.button.target === '_blank'
                      ? 'noopener noreferrer'
                      : undefined
                  ">
                  {{ button.button.title }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from '@/stores/main'
import useSmoothScroll from '@/components/composables/useSmoothScroll'
import { usePopUp } from '@/components/composables/usePopUp'

const store = useStore()
const { locoScroll } = useSmoothScroll()
const { showPopUp, closePopUp } = usePopUp()
const closeButton = ref(null)

const popupOptions = computed(() => store.acfOptions()?.intro_pop_up || {})

const handleKeydown = (event) => {
  if (event.key === 'Escape' && showPopUp.value) {
    closePopUp()
  }
}

watch(showPopUp, (value) => {
  if (value) {
    locoScroll.value?.stop?.()
    nextTick(() => closeButton.value?.focus())
    return
  }

  locoScroll.value?.start?.()
})

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  locoScroll.value?.start?.()
})
</script>

<style lang="scss">
.intro-popup-backdrop {
  @apply fixed inset-0 z-[100] bg-black/60;
}

.intro-popup {
  @apply fixed inset-0 z-[101] flex items-center justify-center p-3 md:p-5;

  .intro-popup-content {
    @apply relative max-h-[90%] w-full max-w-[1000px] overflow-y-auto rounded-[20px] md:h-[80%] md:max-h-[595px] md:rounded-[40px];
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    background: linear-gradient(180deg, rgb(0 135 209), rgb(0 69 107));

    .intro-popup-scroll-body {
      @apply flex min-h-full w-full px-6 py-[70px] md:px-12 lg:px-20;

      .intro-popup-copy {
        @apply mx-auto flex w-full max-w-[650px] flex-col items-center gap-10 text-center justify-between;

        h2 {
          @apply m-0 font-FKDisplay text-[24px] tracking-[7%] uppercase font-bold leading-[30px] text-white;
        }

        .intro-popup-image {
          @apply m-0 h-auto max-w-[645px] w-full object-contain;
        }

        .intro-popup-action {
          @apply flex flex-col gap-5 items-center w-full justify-center;

          .intro-popup-button {
            @apply flex min-h-[54px] w-full max-w-[290px] items-center justify-center bg-white px-[30px] py-4
          font-FKGrotesk text-[18px] font-medium leading-[22px] tracking-[-0.36px] text-[#0087D1]
          transition-opacity duration-200 hover:opacity-90 focus-visible:outline focus-visible:outline-2
          focus-visible:outline-offset-4 focus-visible:outline-white;
            transition: opacity 200ms ease, border-radius 200ms ease;
            border-radius: 30px 0;

            &:hover {
              border-radius: 0 30px;
            }
          }
        }
      }
    }

    .intro-popup-close {
      @apply absolute right-3 h-[35px] w-[35px] top-3 z-2 flex cursor-pointer flex-col items-center justify-center rounded-full border border-white bg-transparent transition-colors md:right-4 md:top-4;
      @apply md:h-[50px] md:w-[50px];

      span {
        @apply block h-[2px] w-[18px] bg-white transition-colors md:w-[28px];

        &:first-child {
          @apply translate-y-[1px] rotate-45;
        }

        &:last-child {
          @apply -translate-y-[1px] -rotate-45;
        }
      }

      &:hover,
      &:focus-visible {
        @apply bg-white/10;
      }
    }
  }
}
</style>
